/**
 * Consent-independent page-engagement capture, shared by the marketing site
 * and the app SPA.
 *
 * THE UNIT IS A PAGE VISIT, not a tab load. On the marketing site the two are
 * the same thing — every URL is its own document. In the SPA they are not: a
 * person who lands on `/` and reads three Requests has had four page visits
 * inside one document, and measuring the tab would report all four as one long
 * stay on the homepage. So the SPA calls `endPageVisit()` on every client route
 * change, which closes the current visit and opens the next.
 *
 * A visit can be REPORTED IN SEGMENTS. iOS Safari frequently never fires
 * `pagehide`, so `visibilitychange → hidden` is the last moment a beacon is
 * guaranteed to go out; if the person comes back, the same visit continues.
 * Every segment therefore carries the visit's `visit_id`, and a reader
 * reconstructs the visit by summing `visible_ms` / `dwell_ms` / `clicks` and
 * taking the MAX of `max_scroll_pct` across the segments sharing that id.
 * Without the id, a 60-second read interrupted once would read as two
 * 30-second reads and every median would be wrong.
 *
 * PostHog gives us `$pageleave` with a duration, but only for the visitors who
 * accepted cookies — which is exactly the wrong half of the population when the
 * question is "what do people do BEFORE they sign up". This module is the
 * consent-free twin: it drops nothing on the device (no cookie, no storage) and
 * reports, once per page load, how long the page was open, how much of it was
 * scrolled, and how many interactive taps happened.
 *
 * Two beacons per page load, at most:
 *   - a scroll-depth beacon at each of 25 / 50 / 75 / 100 % first reached
 *     (the reliable half — it fires while the page is alive)
 *   - one engagement beacon on teardown, carrying dwell / visible time /
 *     max scroll / click count (the complete half — it can be lost if the
 *     browser kills the page, which is why the milestones exist separately)
 *
 * The caller supplies `emit`, so each app names the events in its own
 * namespace (`marketing.*` vs `app.*`) using its own beacon transport.
 *
 * A scroll proportion is a ratio of three numbers, and only one of them is
 * trustworthy at any moment. `scrollY` is a fact the moment it is read. The
 * viewport height is not — an in-app browser reports 0-135px while its toolbar
 * animates in (see `viewport.ts`) — and neither is the document height, which
 * grows as fonts and images land. So only `scrollY` is accumulated here; the
 * proportion is computed once, at teardown, off a viewport and a document that
 * have stopped moving.
 *
 * That ordering is what keeps the number honest. Reading the ratio early instead
 * meant a document not yet laid out looked SHORTER than the viewport, which this
 * module reads as "the whole page fits, so it was all seen" — 100%. Because the
 * maximum only ever moved up, that 100 was permanent, and it released all four
 * milestones in one frame: a visitor who saw one screen for 1.5s was recorded as
 * having read to the bottom.
 *
 * `scroll_depth` and `max_scroll_pct` are therefore deliberately not the same
 * fact. A milestone means the visitor SCROLLED there, and only fires on a page
 * long enough to scroll. `max_scroll_pct: 100` on a page shorter than the
 * viewport means everything was on screen without scrolling. Both are true at
 * once and a funnel asking "did they scroll past 50%" wants the first.
 */

import { documentHeight, viewportSize } from './viewport';

export interface PageEngagementSnapshot {
  /**
   * The visit this segment belongs to. Segments sharing an id are one visit:
   * sum the durations and click counts, take the max of the depth.
   */
  visit_id: string;
  /** Wall-clock ms of THIS segment. */
  dwell_ms: number;
  /** Of that, ms the tab was actually visible. */
  visible_ms: number;
  /** Deepest scroll reached in the VISIT so far, 0-100. 100 on a page shorter than the viewport. */
  max_scroll_pct: number;
  /** Taps on any interactive element in this segment (same selector the click beacons use). */
  clicks: number;
  /**
   * Viewport and document size at teardown. Without them a scroll percentage is
   * uninterpretable: "reached 6%" on a 10,000px page means the visitor saw the
   * first screen and nothing else, which is a completely different fact from 6%
   * of a 1,200px page. In-app browsers (Facebook / Instagram) also give far less
   * height than Safari, so whether anything sat below the fold is device-specific.
   */
  vw: number;
  vh: number;
  doc_h: number;
  /** Deepest pixel of the document ever on screen — max_scroll_pct in absolutes. */
  max_scroll_px: number;
}

export interface PageEngagementOptions {
  /** Fired once per milestone per VISIT with the depth reached (25 | 50 | 75 | 100). */
  onScrollDepth: (depth: number) => void;
  /** Fired once per reported segment; `visit_id` says which visit it belongs to. */
  onEngagement: (snapshot: PageEngagementSnapshot) => void;
}

const MILESTONES = [25, 50, 75, 100];
// `[role="button"]` (2026-09-08): the Banyan's lights are SVG groups with the
// role, not <button>s, so a visit that tapped lights and nothing else summed
// to clicks=0 and read as an untouched page.
const INTERACTIVE_SELECTOR = 'a[href], button, summary, input, select, textarea, [data-log], [role="button"]';

let installed = false;

/** Current scroll offset — a fact at the moment it is read, unlike the viewport. */
function scrollY(): number {
  return Math.max(0, Math.round(window.scrollY || document.documentElement.scrollTop || 0));
}

/**
 * Deepest document pixel that has been on screen, from the deepest scroll offset
 * reached. Exported because this is the whole arithmetic of "how much did they
 * see", and it is where getting the inputs' timing wrong used to turn a
 * one-screen visit into 100%.
 *
 * A document no taller than the viewport was entirely on screen from the start.
 * That is only true of a document that has finished laying out, which is why
 * callers must not feed this a height read before the page settled.
 */
export function deepestVisiblePx(maxScrollY: number, vh: number, docH: number): number {
  if (docH <= 0) return 0;
  if (docH <= vh) return docH;
  return Math.min(docH, Math.max(0, maxScrollY) + Math.max(0, vh));
}

/** `deepestVisiblePx` as a whole percentage of the document. */
export function scrollPct(maxScrollY: number, vh: number, docH: number): number {
  if (docH <= 0) return 100;
  return Math.max(0, Math.min(100, Math.round((deepestVisiblePx(maxScrollY, vh, docH) / docH) * 100)));
}

/**
 * Module-level visit state. It lives here rather than in a closure because
 * `endPageVisit()` is called from the router, outside the installer.
 *
 * VISIT-scoped: id, deepest scroll, milestones already fired.
 * SEGMENT-scoped: the clock and the click count, which reset every time a
 * segment is reported so the same seconds are never counted twice.
 */
let emit: PageEngagementOptions | null = null;
let visitId = '';
let maxScrollY = 0;
const fired = new Set<number>();
let segmentStartedAt = 0;
let segmentOpen = false;
let clicks = 0;
let visibleMs = 0;
let visibleSince = 0;
let scheduled = false;

function newVisitId(): string {
  return `v_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`;
}

function beginSegment(): void {
  segmentStartedAt = Date.now();
  segmentOpen = true;
  clicks = 0;
  visibleMs = 0;
  visibleSince = document.visibilityState === 'hidden' ? 0 : segmentStartedAt;
}

/**
 * Close the open segment and report it. The proportion is computed HERE, at
 * teardown, off a viewport and a document that have stopped moving — reading
 * it earlier is what used to turn a one-screen visit into 100%.
 */
function reportSegment(): void {
  if (!segmentOpen || !emit) return;
  segmentOpen = false;
  if (visibleSince) { visibleMs += Date.now() - visibleSince; visibleSince = 0; }
  const { vw, vh } = viewportSize();
  const docH = documentHeight();
  const y = Math.max(maxScrollY, scrollY());
  maxScrollY = y;
  try {
    emit.onEngagement({
      visit_id: visitId,
      dwell_ms: Date.now() - segmentStartedAt,
      visible_ms: visibleMs,
      max_scroll_pct: scrollPct(y, vh, docH),
      clicks,
      vw,
      vh,
      doc_h: docH,
      max_scroll_px: deepestVisiblePx(y, vh, docH),
    });
  } catch { /* telemetry never breaks a page */ }
}

/**
 * The visitor left this page for another one inside the same document. Closes
 * the visit and opens the next: a fresh id, depth back to zero, milestones
 * armed again. The SPA router calls this on every client route change; a
 * multi-page site never needs it, because the document teardown IS the end of
 * the visit.
 */
export function endPageVisit(): void {
  if (!emit) return;
  reportSegment();
  visitId = newVisitId();
  maxScrollY = 0;
  fired.clear();
  beginSegment();
}

/**
 * Install the listeners (idempotent). Safe to call before first paint: nothing
 * is measured until the visitor scrolls or the page is torn down.
 */
export function initPageEngagement(options: PageEngagementOptions): void {
  if (installed || typeof window === 'undefined' || typeof document === 'undefined') return;
  installed = true;
  emit = options;
  visitId = newVisitId();
  beginSegment();

  const measure = () => {
    scheduled = false;
    const y = scrollY();
    if (y > maxScrollY) maxScrollY = y;

    // Milestones are about having scrolled somewhere, so a page with nothing to
    // scroll has none to report. This is also what keeps a document that has not
    // finished laying out — briefly shorter than the viewport — from releasing
    // every milestone at once.
    const { vh } = viewportSize();
    const docH = documentHeight();
    if (docH <= vh || docH <= 0) return;

    const pct = scrollPct(y, vh, docH);
    for (const milestone of MILESTONES) {
      if (pct >= milestone && !fired.has(milestone)) {
        fired.add(milestone);
        try { options.onScrollDepth(milestone); } catch { /* telemetry never breaks a page */ }
      }
    }
  };

  const onScroll = () => {
    if (scheduled) return;
    scheduled = true;
    window.requestAnimationFrame(measure);
  };

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });
  document.addEventListener(
    'click',
    (event) => {
      const target = event.target as Element | null;
      if (target?.closest?.(INTERACTIVE_SELECTOR)) clicks += 1;
    },
    { capture: true },
  );

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      // iOS Safari frequently never fires `pagehide`; hidden is the last moment
      // we are guaranteed to run, so the segment goes out here. The visit is
      // not over — coming back opens its next segment under the same id.
      reportSegment();
    } else if (!segmentOpen) {
      beginSegment();
    } else if (!visibleSince) {
      // A page loaded in a background tab opened its segment with no clock (beginSegment): coming to the front starts
      // it (release check 2026-10-27 — until then such a visit reported visible_ms 0 however long it was read).
      visibleSince = Date.now();
    }
  });
  // Desktop browsers do fire pagehide, and it is the more accurate teardown
  // point when they do. A segment already reported is not reported twice.
  window.addEventListener('pagehide', reportSegment);
}
